import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentProfileUpdate, useCurrentProfilePhoto } from '../contexts/ProfileContext';
import settingsApi from '../api/settingsApi';
import api from '../api/axios';
import LoadingSpinner from '../components/LoadingSpinner';
import PageTransition from '../components/PageTransition';
import Toast from '../components/Toast';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import ConfirmDialog from '../components/ConfirmDialog';
import ProfileUpdateSuccess from '../components/ProfileUpdateSuccess';
import ProfilePreview from '../components/ProfilePreview';
import LoginInfoDisplay from '../components/LoginInfoDisplay';
import { useToast } from '../hooks/useToast';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function Settings() {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { user, updateProfile, changePassword } = useAuth();
  const updateProfilePhoto = useCurrentProfileUpdate();
  const profilePhoto = useCurrentProfilePhoto();
  const { toasts, success, error, warning, removeToast } = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [accountInfo, setAccountInfo] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Administrator'
  });
  const [editMode, setEditMode] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showProfileConfirm, setShowProfileConfirm] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successType, setSuccessType] = useState('profile');
  const [originalAccountInfo, setOriginalAccountInfo] = useState({});
  const [backupFile, setBackupFile] = useState(null);

  // Fetch current admin data
  const fetchAdminData = async () => {
    try {
      if (user) {
        const userData = {
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '+62 812-3456-7890',
          role: 'Administrator'
        };
        setAccountInfo(userData);
        setOriginalAccountInfo(userData);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      error('Failed to load profile data');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1 * 1024 * 1024) { // Reduce to 1MB
        warning('File size must be less than 1MB');
        e.target.value = '';
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        warning('Please select a valid image file (JPG, PNG, GIF, WebP)');
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target.result;
        if (result && typeof result === 'string' && result.startsWith('data:image/')) {
          // Compress image before preview
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Resize if too large
            const maxSize = 500;
            if (width > height && width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            } else if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to compressed JPEG
            const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
            setPreviewUrl(compressedDataUrl);
          };
          img.src = result;
        } else {
          warning('Invalid image file');
          setSelectedFile(null);
          setPreviewUrl('');
          e.target.value = '';
        }
      };
      reader.onerror = () => {
        warning('Failed to read the selected file');
        setSelectedFile(null);
        setPreviewUrl('');
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setPreviewUrl('');
    }
  };

  const handleSave = async () => {
    if (!selectedFile) {
      warning('Please select a file first');
      return;
    }
    
    setLoading(true);
    try {
      // Create FormData to upload file
      const formData = new FormData();
      formData.append('profile_photo', selectedFile);
      
      // Upload to backend
      const response = await api.post('/upload/profile-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && (response.data.url || response.data.photoUrl)) {
        // Update profile photo with URL from backend
        const photoUrl = response.data.url || response.data.photoUrl;
        
        // Clean up old base64 data from localStorage
        const oldPhoto = localStorage.getItem('profilePhoto');
        if (oldPhoto && oldPhoto.startsWith('data:image')) {
          localStorage.removeItem('profilePhoto');
        }
        
        updateProfilePhoto(photoUrl);
        setSuccessType('photo');
        setShowSuccessNotification(true);
        success('Profile photo updated successfully!');
        setSelectedFile(null);
        setPreviewUrl('');
        
        // Clear the file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        error('Failed to get photo URL from server');
      }
    } catch (uploadError) {
      console.error('Upload error:', uploadError);
      error('Failed to update profile photo: ' + (uploadError.response?.data?.message || uploadError.message));
    } finally {
      setLoading(false);
    }
  };

  const validateProfileData = () => {
    if (!passwordData.currentPassword) {
      warning('Please enter your current password to save changes');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(accountInfo.email)) {
      warning('Please enter a valid email address');
      return false;
    }

    // Validate name
    if (accountInfo.name.trim().length < 2) {
      warning('Name must be at least 2 characters long');
      return false;
    }

    return true;
  };

  const handleAccountSave = () => {
    if (validateProfileData()) {
      setShowProfileConfirm(true);
    }
  };

  const confirmAccountSave = async () => {
    setShowProfileConfirm(false);
    setAccountLoading(true);
    
    try {
      // Verify current password first
      const storedPassword = localStorage.getItem('adminPassword') || 'baru123';
      if (passwordData.currentPassword !== storedPassword) {
        error('Current password is incorrect');
        setAccountLoading(false);
        return;
      }

      // Update profile using AuthContext
      const result = updateProfile({
        name: accountInfo.name,
        email: accountInfo.email,
        phone: accountInfo.phone
      });
      
      if (result.success) {
        // Update original data to reflect changes
        setOriginalAccountInfo({
          name: accountInfo.name,
          email: accountInfo.email,
          phone: accountInfo.phone,
          role: 'Administrator'
        });
        
        // Force re-fetch data to update display
        await fetchAdminData();
        
        setSuccessType('profile');
        setShowSuccessNotification(true);
        success('Account information updated successfully! You can now login with your new email.');
        setEditMode(false);
        setPasswordData({...passwordData, currentPassword: ''});
      } else {
        error(result.error || 'Failed to update account information');
      }
    } catch (updateError) {
      console.error('Update error:', updateError);
      error('Failed to update account information');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleAccountCancel = () => {
    setAccountInfo(originalAccountInfo);
    setEditMode(false);
    setPasswordData({...passwordData, currentPassword: ''});
  };

  const validatePasswordData = () => {
    if (!passwordData.currentPassword) {
      warning('Please enter your current password');
      return false;
    }
    
    if (!passwordData.newPassword) {
      warning('Please enter a new password');
      return false;
    }
    
    if (!passwordData.confirmPassword) {
      warning('Please confirm your new password');
      return false;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      warning('New passwords do not match');
      return false;
    }
    
    if (passwordData.newPassword.length < 6) {
      warning('New password must be at least 6 characters');
      return false;
    }

    return true;
  };

  const handlePasswordChange = () => {
    if (validatePasswordData()) {
      setShowPasswordConfirm(true);
    }
  };

  const confirmPasswordChange = async () => {
    setShowPasswordConfirm(false);
    setPasswordLoading(true);
    
    try {
      // Use AuthContext changePassword method
      const result = changePassword(passwordData.currentPassword, passwordData.newPassword);
      
      if (result.success) {
        setSuccessType('password');
        setShowSuccessNotification(true);
        success('Password changed successfully! Please use the new password for future logins.');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        error(result.error || 'Failed to change password');
      }
    } catch (passwordError) {
      console.error('Password change error:', passwordError);
      error('Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <PageTransition>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-6"
      >
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300`}
        >
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{t('settings')}</h1>
          <p className={`mt-1 ${isDark ? 'text-white' : 'text-gray-600'}`}>{t('manage_account_settings')}</p>
        </motion.div>

        {/* Profile Settings */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300`}
        >
          <h2 className={`text-xl font-bold mb-6 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <i className={`fas fa-user-circle mr-3 ${isDark ? 'text-white' : 'text-blue-600'}`}></i>
            {t('profile_settings')}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <label className={`block text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('current_profile_photo')}</label>
              <div className="flex items-center space-x-4">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg overflow-hidden border-4 border-blue-200"
                >
                  <img 
                    src={profilePhoto} 
                    alt="Current Profile" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://i.pinimg.com/736x/fb/3f/53/fb3f5378975c7265117a0199ebf40266.jpg';
                    }}
                  />
                </motion.div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-600'}`}>{t('photo_appears_header')}</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-white' : 'text-gray-500'}`}>{t('recommended_square')}</p>
                </div>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('choose_new_photo')}</label>
              <div className="space-y-4">
                <motion.input
                  whileFocus={{ scale: 1.02 }}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={`w-full p-3 border-2 border-dashed rounded-xl transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium ${isDark ? 'border-gray-600 bg-gray-700/50 hover:border-gray-500 file:bg-gray-600 file:text-white hover:file:bg-gray-500' : 'border-blue-300 bg-white/50 hover:border-blue-400 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'}`}
                />
                <p className={`text-xs ${isDark ? 'text-white' : 'text-gray-500'}`}>{t('choose_image_file')} (Max 1MB, akan dikompres otomatis)</p>
                
                <AnimatePresence>
                  {previewUrl && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('preview')}</label>
                      <motion.div 
                        whileHover={{ scale: 1.05 }}
                        className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg overflow-hidden border-4 border-blue-200"
                      >
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSave}
                  disabled={loading || !selectedFile}
                  className={`w-full text-white px-6 py-3 rounded-xl font-semibold focus:ring-4 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-200'}`}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">{t('updating')}</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload mr-2"></i>
                      {t('update_photo')}
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Account Information */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-xl font-bold flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <i className={`fas fa-info-circle mr-3 ${isDark ? 'text-white' : 'text-blue-600'}`}></i>
              {t('account_information')}
            </h2>
            {!editMode ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setEditMode(true)}
                className={`text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center ${isDark ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                <i className="fas fa-edit mr-2"></i>
                {t('edit')}
              </motion.button>
            ) : (
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAccountSave}
                  disabled={accountLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all duration-200 flex items-center disabled:opacity-50"
                >
                  {accountLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {t('save')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAccountCancel}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-600 transition-all duration-200 flex items-center"
                >
                  <i className="fas fa-times mr-2"></i>
                  {t('cancel')}
                </motion.button>
              </div>
            )}
          </div>

          <AnimatePresence mode="wait">
            {dataLoading ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center py-8"
              >
                <LoadingSpinner size="md" type="dots" />
              </motion.div>
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('name')}</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="text"
                    value={accountInfo.name}
                    onChange={(e) => setAccountInfo({...accountInfo, name: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl transition-all ${
                      editMode 
                        ? 'bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                        : 'bg-gray-50 text-gray-600'
                    }`}
                    disabled={!editMode}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('email')}</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="email"
                    value={accountInfo.email}
                    onChange={(e) => setAccountInfo({...accountInfo, email: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl transition-all ${
                      editMode 
                        ? 'bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                        : 'bg-gray-50 text-gray-600'
                    }`}
                    disabled={!editMode}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('phone')}</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    type="tel"
                    value={accountInfo.phone}
                    onChange={(e) => setAccountInfo({...accountInfo, phone: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl transition-all ${
                      editMode 
                        ? 'bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                        : 'bg-gray-50 text-gray-600'
                    }`}
                    disabled={!editMode}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('role')}</label>
                  <input
                    type="text"
                    value={accountInfo.role}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600"
                    disabled
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Profile Preview */}
          <ProfilePreview
            currentData={originalAccountInfo}
            newData={accountInfo}
            isVisible={editMode}
          />

          {/* Login Info Display */}
          <LoginInfoDisplay />

          <AnimatePresence>
            {editMode && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200"
              >
                <p className="text-sm text-yellow-700">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  {t('enter_password_to_save')}
                </p>
                <motion.input
                  whileFocus={{ scale: 1.02 }}
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className="w-full mt-3 p-3 border border-yellow-300 rounded-xl bg-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all"
                  placeholder={t('enter_password_confirm')}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Database Backup & Restore */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300`}
        >
          <h2 className={`text-xl font-bold mb-6 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <i className={`fas fa-database mr-3 ${isDark ? 'text-white' : 'text-blue-600'}`}></i>
            {t('database_backup_restore')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Export Backup */}
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                <i className="fas fa-download mr-2 text-green-600"></i>
                {t('export_database')}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('download_sql_backup')}
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={async () => {
                  try {
                    setLoading(true);
                    const response = await api.get('/backup/export', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `sewaapartemen_backup_${new Date().toISOString().split('T')[0]}.sql`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    success(t('backup_downloaded'));
                  } catch (err) {
                    error(t('failed_export_backup'));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="w-full bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-all duration-200 flex items-center justify-center disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">{t('exporting')}</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-export mr-2"></i>
                    {t('export_sql_backup')}
                  </>
                )}
              </motion.button>
            </div>

            {/* Import Restore */}
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                <i className="fas fa-upload mr-2 text-orange-600"></i>
                {t('import_database')}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('restore_from_sql')}
              </p>
              <div className="space-y-3">
                <input
                  type="file"
                  accept=".sql"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (!file.name.endsWith('.sql')) {
                        warning(t('select_valid_sql'));
                        e.target.value = '';
                        return;
                      }
                      setBackupFile(file);
                    }
                  }}
                  className={`w-full p-3 border-2 border-dashed rounded-xl transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium ${isDark ? 'border-gray-600 bg-gray-700/50 hover:border-gray-500 file:bg-gray-600 file:text-white hover:file:bg-gray-500' : 'border-orange-300 bg-white/50 hover:border-orange-400 file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100'}`}
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={async () => {
                    if (!backupFile) {
                      warning(t('select_sql_file'));
                      return;
                    }
                    if (!window.confirm(t('confirm_restore'))) {
                      return;
                    }
                    try {
                      setLoading(true);
                      const formData = new FormData();
                      formData.append('sql_file', backupFile, backupFile.name);
                      
                      const response = await api.post('/backup/import', formData);
                      
                      success(t('database_restored'));
                      setBackupFile(null);
                      const fileInput = document.querySelector('input[type="file"][accept=".sql"]');
                      if (fileInput) fileInput.value = '';
                    } catch (err) {
                      console.error('Import error:', err);
                      error(err.response?.data?.message || t('failed_restore_database'));
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !backupFile}
                  className="w-full bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-700 focus:ring-4 focus:ring-orange-200 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">{t('restoring')}</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-import mr-2"></i>
                      {t('restore_from_sql_btn')}
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>

          <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>
              <i className="fas fa-exclamation-triangle mr-2"></i>
              <strong>{t('warning')}:</strong> {t('backup_warning')}
            </p>
          </div>
        </motion.div>

        {/* Change Password */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300`}
        >
          <h2 className={`text-xl font-bold mb-6 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <i className={`fas fa-lock mr-3 ${isDark ? 'text-white' : 'text-blue-600'}`}></i>
            {t('change_password')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                {t('current_password')} <span className="text-red-500">*</span>
              </label>
              <motion.input
                whileFocus={{ scale: 1.02 }}
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder={t('enter_current_password')}
              />
            </div>
            
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('new_password')}</label>
              <motion.input
                whileFocus={{ scale: 1.02 }}
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder={t('enter_new_password')}
              />
              <PasswordStrengthIndicator password={passwordData.newPassword} />
            </div>
            
            <div>
              <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-700'}`}>{t('confirm_new_password')}</label>
              <motion.input
                whileFocus={{ scale: 1.02 }}
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder={t('confirm_new_password_placeholder')}
              />
            </div>
          </div>

          <div className="mt-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handlePasswordChange}
              disabled={passwordLoading}
              className={`text-white px-6 py-3 rounded-xl font-semibold focus:ring-4 transition-all duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-200'}`}
            >
              {passwordLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">{t('changing_password')}</span>
                </>
              ) : (
                <>
                  <i className="fas fa-key mr-2"></i>
                  {t('change_password_btn')}
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Confirmation Dialogs */}
        <ConfirmDialog
          isOpen={showProfileConfirm}
          title={t('confirm_profile_update')}
          message={t('confirm_profile_message')}
          confirmText={t('update_profile')}
          onConfirm={confirmAccountSave}
          onCancel={() => setShowProfileConfirm(false)}
          type="info"
        />

        <ConfirmDialog
          isOpen={showPasswordConfirm}
          title={t('confirm_password_change')}
          message={t('confirm_password_message')}
          confirmText={t('change_password_btn')}
          onConfirm={confirmPasswordChange}
          onCancel={() => setShowPasswordConfirm(false)}
          type="warning"
        />

        {/* Success Notification */}
        <ProfileUpdateSuccess
          show={showSuccessNotification}
          type={successType}
          onClose={() => setShowSuccessNotification(false)}
        />

        {/* Toast Notifications */}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </motion.div>
    </PageTransition>
  );
}